import { useEffect, useRef, useState } from 'react';
import { formatDate, FORMAT } from '../../../../../lib/date';
import request from '../../../../../lib/request';

const CardioDialog = ({
  completeDialogRef,
  closeDialog,
  isDialogOpen,
  routine,
  refreshLog,
}) => {
  const bottomRef = useRef(null);

  const [dateTime, setDateTime] = useState('');
  const [duration, setDuration] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [loading, setLoading] = useState(false);
  const [emojiExpression, setEmojiExpression] = useState([]);
  const [note, setNote] = useState('');
  const [feeling, setFeeling] = useState([]);
  const [distance, setDistance] = useState(0);
  const [steps, setSteps] = useState(0);

  useEffect(() => {
    const clickOutsideDialog = (e) => {
      if (
        completeDialogRef.current.contains(e.target) &&
        !bottomRef.current.contains(e.target)
      ) {
        handleCancel();
      }
    };

    document.addEventListener('click', clickOutsideDialog);

    return () => {
      document.removeEventListener('click', clickOutsideDialog);
    };
  }, []);

  useEffect(() => {
    if (isDialogOpen) {
      getEmojiExpression();

      if (routine?.routine) {
        const currentRoutine = routine?.routine;

        const hours = Math.floor(currentRoutine.duration / 3600);
        const minutes = Math.floor((currentRoutine.duration % 3600) / 60);
        const seconds = Math.floor((currentRoutine.duration % 3600) % 60);
        setDuration({
          hours,
          minutes,
          seconds,
        });
        setDistance(currentRoutine.distance ? currentRoutine.distance : 0);
        setSteps(currentRoutine.steps ? currentRoutine.steps : 0);

        setDateTime(
          `${formatDate(new Date())}T${formatDate(new Date(), FORMAT.HOURMIN)}`
        );
      }
    } else {
      resetStates();
    }
  }, [isDialogOpen, routine]);

  const getEmojiExpression = async () => {
    try {
      const { result } = await request.get('/static/emoji-expression');
      setEmojiExpression(result);
    } catch (err) {
      console.log(err);
    }
  };

  const resetStates = () => {
    setDateTime('');
    setDuration({ hours: 0, minutes: 0, seconds: 0 });
    setDistance(0);
    setSteps(0);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const logDateTime = dateTime.split('T');
      const logDate = logDateTime[0];
      const logTime = logDateTime[1];
      const logs = [];
      const numDistance = !isNaN(Number(distance)) ? Number(distance) : 0;
      const numSteps = !isNaN(Number(steps)) ? Number(steps) : 0;

      const data = {
        workout: routine.routine.title,
        logDate,
        logTime,
        note,
        feeling: feeling.join(','),
        type: 'CARDIO',
        duration:
          duration.hours * 60 * 60 + duration.minutes * 60 + duration.seconds,
        logs,
        programRoutineId: routine.routineId,
        distance: numDistance,
        steps: numSteps,
      };

      const { result } = await request.post(`/workout/record`, data);
      if (!result) {
        return alert('Fail to record');
      }

      resetStates();
      closeDialog();
      refreshLog();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (
      window.confirm(
        'You can manually record your workout log on "Workout Tracker" section.'
      )
    ) {
      resetStates();
      closeDialog();
    }
  };

  const handleChangeNote = (e) => {
    setNote(e.target.value);
  };

  const handleSelectFeeling = (emoji) => {
    if (feeling.includes(emoji)) {
      setFeeling(feeling.filter((item) => item !== emoji));
    } else if (feeling.length < 3) {
      setFeeling([...feeling, emoji]);
    } else {
      alert('You selected 3 best feelings.');
    }
  };

  const handleChangeDuration = (e) => {
    setDuration({
      ...duration,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <dialog
      ref={completeDialogRef}
      className='w-full h-full max-w-lg max-h-full bg-transparent'
      onCancel={handleCancel}
    >
      <div
        ref={bottomRef}
        className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 pt-6 pb-3 flex flex-col max-h-[90%] overflow-y-auto scrollbar-hidden`}
      >
        <div className='flex items-center justify-between mb-4 text-white'>
          <h1 className='text-xl font-bold flex flex-wrap gap-1'>
            {routine?.routine?.title}
          </h1>
          <button
            onClick={handleCancel}
            className='text-2xl'
          >
            🅧
          </button>
        </div>
        <div className='space-y-2 mt-4'>
          <div className='flex items-center justify-between gap-2'>
            <label className='text-white font-semibold'>Date</label>
            <input
              type='datetime-local'
              max={formatDate(new Date(), FORMAT.DATEHOURMIN)}
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              className='rounded-full px-2 py-1 bg-tertiary  text-white'
            />
          </div>
          <div className='flex items-center justify-between gap-2'>
            <label className='text-white font-semibold'>Duration</label>
            <div className='text-white flex items-center gap-2'>
              <div className='flex items-center gap-2'>
                <select
                  name='hours'
                  value={duration.hours}
                  onChange={handleChangeDuration}
                  className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-16 text-center'
                >
                  {Array.from({ length: 25 }, (_, i) => (
                    <option
                      key={i}
                      value={i}
                    >
                      {i}
                    </option>
                  ))}
                </select>
                <span>h</span>
              </div>
              <div className='flex items-center gap-2'>
                <select
                  name='minutes'
                  value={duration.minutes}
                  onChange={handleChangeDuration}
                  className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-16 text-center'
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option
                      key={i}
                      value={i}
                    >
                      {i}
                    </option>
                  ))}
                </select>
                <span>min</span>
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between gap-2 mb-4'>
            <label className='text-white font-semibold'>Distance</label>
            <div className='text-white flex items-center gap-2'>
              <div className='flex items-center gap-2'>
                <input
                  type='number'
                  name='distance'
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-20 text-center'
                />
                <span className='min-w-10 text-right'>km</span>
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between gap-2 mb-4'>
            <label className='text-white font-semibold'>Steps</label>
            <div className='text-white flex items-center gap-2'>
              <div className='flex items-center gap-2'>
                <input
                  type='number'
                  name='steps'
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-20 text-center'
                />
                <span className='min-w-10 text-right'>steps</span>
              </div>
            </div>
          </div>
          <div className='mb-4'>
            <div className='text-white font-semibold'>
              <span className='mr-2'>How was today's workout?</span>
              <span className='text-xl space-x-1'>
                {Array.from({ length: 3 }, (_, i) => (
                  <span key={i}>{feeling[i] ? feeling[i] : '🫥'}</span>
                ))}
              </span>
            </div>
            <div className='text-xs text-white/50 mb-1'>
              Select 1-3 best feelings
            </div>
            <div className='flex items-center gap-4 py-2 overflow-x-scroll scrollbar-hidden mb-4'>
              {emojiExpression.map((item) => (
                <button
                  key={item.emoji}
                  className={`flex-shrink-0 size-[72px] flex flex-col items-center justify-center gap-1 rounded-xl p-1 ${
                    feeling.includes(item.emoji)
                      ? 'bg-secondary border-2 border-accent'
                      : 'bg-tertiary'
                  }`}
                  onClick={() => handleSelectFeeling(item.emoji)}
                >
                  <span className='text-xl'>{item.emoji}</span>
                  <span className='text-[10px] text-white/70 leading-none'>
                    {item.meaning}
                  </span>
                </button>
              ))}
            </div>
            <div className='flex flex-col gap-2'>
              <label className='text-white font-semibold'>Note</label>
              <textarea
                value={note}
                rows={2}
                onChange={handleChangeNote}
                className='rounded-xl px-3 py-2 bg-tertiary  text-white resize-none'
              />
            </div>
          </div>
        </div>
        <div className='mt-4'>
          <button
            className='bg-primary text-white py-3 rounded-full w-full'
            onClick={handleSubmit}
            disabled={loading}
          >
            Complete
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default CardioDialog;
